export type DefineModelField =
  | string
  | { name: string; type?: string; pk?: boolean; fk?: boolean; nullable?: boolean; unique?: boolean; default?: string };
export type DefineServiceEndpoint = string | { name: string; description?: string };
export type DefineModel = { name: string; description?: string; fields?: DefineModelField[] };
export type DefineService = { name: string; description?: string; endpoints?: DefineServiceEndpoint[] };
export type DefineComponent = { name: string; description?: string };

export type DefineDefinition = {
  name: string;
  description: string;
  domain?: string;
  targetUsers?: string;
  budget?: string;
  teamSize?: string;
  timeline?: string;

  successCriteria: string[];
  goals: string[];
  features: string[];
  constraints: string[];
  stakeholders: string[];
  assumptions: string[];

  // Non-functional requirements
  nonFunctionalRequirements: {
    performance: string;
    scalability: string;
    availability: string;
    security: string;
    privacy: string;
    compliance: string;
    accessibility: string;
  };

  // Product definition
  personas: string[];
  userJourneys: string[];
  inScope: string[];
  outOfScope: string[];
  risks: string[];

  // Delivery & planning
  milestones: string[];
  releasePlan?: string;

  // Integrations & ops
  integrations: string[];
  environmentsAndDeployment: {
    hosting: string;
    cicd: string;
    environments: string;
    observability: string;
    backups: string;
  };

  // Governance / engineering notes
  permissionsAndRoles?: string;
  testingStrategy?: string;
  uxNotes?: string;
  keyScreens: string[];

  models: DefineModel[];
  services: DefineService[];
  components: DefineComponent[];

  architectureNotes?: string;
};

export type { ChatMessage, RepoContext } from '../../models/orchestrator-surface.model';
export { compactChatMessages, normalizeRepoContext } from '../../models/orchestrator-surface.model';

export const initialDefineDefinition: DefineDefinition = {
  name: '',
  description: '',
  domain: '',
  targetUsers: '',
  budget: '',
  teamSize: '',
  timeline: '',
  successCriteria: [],
  goals: [],
  features: [],
  constraints: [],
  stakeholders: [],
  assumptions: [],
  nonFunctionalRequirements: {
    performance: '',
    scalability: '',
    availability: '',
    security: '',
    privacy: '',
    compliance: '',
    accessibility: '',
  },
  personas: [],
  userJourneys: [],
  inScope: [],
  outOfScope: [],
  risks: [],
  milestones: [],
  releasePlan: '',
  integrations: [],
  environmentsAndDeployment: {
    hosting: '',
    cicd: '',
    environments: '',
    observability: '',
    backups: '',
  },
  permissionsAndRoles: '',
  testingStrategy: '',
  uxNotes: '',
  keyScreens: [],
  models: [],
  services: [],
  components: [],
  architectureNotes: '',
};

export function hydrateDefinition(raw: Partial<DefineDefinition> | null | undefined): DefineDefinition {
  const r = (raw ?? {}) as Partial<DefineDefinition>;
  return {
    ...initialDefineDefinition,
    ...r,
    successCriteria: (r.successCriteria ?? initialDefineDefinition.successCriteria) as string[],
    goals: (r.goals ?? initialDefineDefinition.goals) as string[],
    features: (r.features ?? initialDefineDefinition.features) as string[],
    constraints: (r.constraints ?? initialDefineDefinition.constraints) as string[],
    stakeholders: (r.stakeholders ?? initialDefineDefinition.stakeholders) as string[],
    assumptions: (r.assumptions ?? initialDefineDefinition.assumptions) as string[],
    personas: (r.personas ?? initialDefineDefinition.personas) as string[],
    userJourneys: (r.userJourneys ?? initialDefineDefinition.userJourneys) as string[],
    inScope: (r.inScope ?? initialDefineDefinition.inScope) as string[],
    outOfScope: (r.outOfScope ?? initialDefineDefinition.outOfScope) as string[],
    risks: (r.risks ?? initialDefineDefinition.risks) as string[],
    milestones: (r.milestones ?? initialDefineDefinition.milestones) as string[],
    integrations: (r.integrations ?? initialDefineDefinition.integrations) as string[],
    keyScreens: (r.keyScreens ?? initialDefineDefinition.keyScreens) as string[],
    nonFunctionalRequirements: { ...initialDefineDefinition.nonFunctionalRequirements, ...((r.nonFunctionalRequirements as any) ?? {}) },
    environmentsAndDeployment: { ...initialDefineDefinition.environmentsAndDeployment, ...((r.environmentsAndDeployment as any) ?? {}) },
    models: (r.models ?? initialDefineDefinition.models) as any,
    services: (r.services ?? initialDefineDefinition.services) as any,
    components: (r.components ?? initialDefineDefinition.components) as any,
  };
}
